import React, { useState, useEffect } from 'react';
import { KeyRound, User, Calendar, Clock, Send, Loader, CheckCircle, XCircle, AlertCircle, LogOut, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import axios from 'axios';

const ABSENCE_TYPES = [
  { value: 'cp', label: 'Congés payés (CP)', icon: '🏖️' },
  { value: 'cp_anciennete', label: 'CP ancienneté', icon: '🏅' },
  { value: 'recup', label: 'Récupération', icon: '🔄' },
  { value: 'maladie', label: 'Arrêt maladie', icon: '🤒' },
  { value: 'formation', label: 'Formation', icon: '📚' },
  { value: 'autre', label: 'Autre', icon: '📋' },
];

const STATUT_BADGE = {
  en_attente: { label: 'En attente', icon: AlertCircle, color: 'bg-amber-100 text-amber-700' },
  approuve: { label: 'Approuvé', icon: CheckCircle, color: 'bg-green-100 text-green-700' },
  refuse: { label: 'Refusé', icon: XCircle, color: 'bg-red-100 text-red-700' },
};

function LoginForm({ onLogin, loading, error }) {
  const [code, setCode] = useState('');

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-100 to-cream-200 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-navy-700 to-navy-900 flex items-center justify-center mx-auto mb-4 text-3xl shadow-lg">
            🤵
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Hello Georges</h1>
          <p className="text-slate-500 text-sm mt-1">Espace Employé</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound size={18} className="text-gold-600" />
            <h2 className="font-semibold text-slate-700">Connexion</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            Entrez votre code personnel (fourni par la direction).
          </p>
          <form onSubmit={e => { e.preventDefault(); onLogin(code.toUpperCase()); }}>
            <input
              type="text"
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder="Ex: GRG-AB2K"
              className="w-full border border-slate-200 rounded-lg px-4 py-3 text-center text-lg font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-navy-300 focus:border-navy-400 mb-3"
              maxLength={8}
              autoFocus
            />
            {error && <p className="text-red-500 text-xs mb-3 text-center">{error}</p>}
            <button
              type="submit"
              disabled={code.length < 6 || loading}
              className="w-full bg-navy-700 hover:bg-navy-800 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
            >
              {loading ? <Loader size={16} className="animate-spin" /> : <KeyRound size={16} />}
              Accéder à mon espace
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          SAS Les Cachettes · Hello Georges
        </p>
      </div>
    </div>
  );
}

function EmployeeDashboard({ data, base, onLogout }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: 'cp', date_debut: '', date_fin: '', jours: '', commentaire: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [absences, setAbsences] = useState(data.absences || []);
  // Planning navigable par semaine
  const [wk, setWk] = useState({ lundi: data.semaine_lundi, planning: data.planning || [], heures: data.heures_planifiees_semaine });
  const [wkLoading, setWkLoading] = useState(false);
  const loadWeek = async (lundiISO) => {
    setWkLoading(true);
    try {
      const res = await axios.get(`${base}${lundiISO ? `?lundi=${lundiISO}` : ''}`);
      setWk({ lundi: res.data.semaine_lundi, planning: res.data.planning || [], heures: res.data.heures_planifiees_semaine });
    } catch { /* noop */ }
    setWkLoading(false);
  };
  const shiftWeek = (delta) => {
    try { const d = parseISO(wk.lundi); d.setDate(d.getDate() + delta * 7); loadWeek(format(d, 'yyyy-MM-dd')); } catch { /* noop */ }
  };
  const currentMonday = (() => { const t = new Date(); const day = t.getDay(); const m = new Date(t); m.setDate(t.getDate() - day + (day === 0 ? -6 : 1)); return format(m, 'yyyy-MM-dd'); })();
  const isCurrentWeek = wk.lundi === currentMonday;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.date_debut || !form.date_fin) return;
    setSubmitting(true);
    try {
      await axios.post(`${base}/absence`, form);
      setSuccess('Demande envoyée ! En attente de validation.');
      setShowForm(false);
      setForm({ type: 'cp', date_debut: '', date_fin: '', jours: '', commentaire: '' });
      // Refresh absences
      const res = await axios.get(base);
      setAbsences(res.data.absences || []);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    }
    setSubmitting(false);
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try { return format(parseISO(d), 'd MMM yyyy', { locale: fr }); } catch { return d; }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-navy-700 to-navy-900 text-white">
        <div className="max-w-2xl mx-auto px-4 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-xl">🤵</div>
            <div>
              <p className="font-bold">Bonjour {data.prenom} !</p>
              <p className="text-cream-200 text-xs">{data.structure_nom} · {data.poste}</p>
            </div>
          </div>
          <button onClick={onLogout} className="text-cream-200 hover:text-white flex items-center gap-1 text-sm">
            <LogOut size={14} /> Déconnexion
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-700 flex items-center gap-2">
            <CheckCircle size={16} /> {success}
          </div>
        )}

        {/* Soldes */}
        <div className={`grid ${data.eligible_cp_anciennete ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
          <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
            <div className="text-3xl font-bold text-green-600">{data.solde_cp}</div>
            <div className="text-xs text-slate-500 mt-1">Jours de CP restants</div>
          </div>
          {data.eligible_cp_anciennete && (
            <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
              <div className="text-3xl font-bold text-amber-600">{data.solde_cp_anciennete || 0}</div>
              <div className="text-xs text-slate-500 mt-1">CP ancienneté</div>
            </div>
          )}
        </div>

        {/* Temps de travail */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
            <h3 className="font-semibold text-slate-700 text-sm flex items-center gap-2">
              <Clock size={14} className="text-gold-600" /> Mon temps de travail
            </h3>
          </div>
          <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="text-center">
              <div className="text-2xl font-bold text-navy-700">{data.heures_hebdo_contrat ?? '—'}<span className="text-sm font-medium text-slate-400">h</span></div>
              <div className="text-[11px] text-slate-500 mt-0.5">Contrat / semaine</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-navy-700">{data.heures_planifiees_semaine ?? 0}<span className="text-sm font-medium text-slate-400">h</span></div>
              <div className="text-[11px] text-slate-500 mt-0.5">Planifiées cette semaine</div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${(data.heures_supp || 0) > 0 ? 'text-amber-600' : 'text-slate-400'}`}>{data.heures_supp || 0}<span className="text-sm font-medium text-slate-400">h</span></div>
              <div className="text-[11px] text-slate-500 mt-0.5">Heures supp.</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-navy-700">{data.heures_formation_annee || 0}<span className="text-sm font-medium text-slate-400">h</span></div>
              <div className="text-[11px] text-slate-500 mt-0.5">Formation (année)</div>
            </div>
          </div>
          {data.solde_rtt > 0 && (
            <div className="px-4 pb-3 -mt-1 text-center text-xs text-slate-500">
              <span className="font-semibold text-navy-700">{data.solde_rtt}</span> jour{data.solde_rtt > 1 ? 's' : ''} de RTT restant{data.solde_rtt > 1 ? 's' : ''}
            </div>
          )}
        </div>

        {/* Bouton nouvelle demande */}
        {!showForm && (
          <button onClick={() => setShowForm(true)}
            className="w-full bg-navy-700 hover:bg-navy-800 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 shadow-sm transition-colors">
            <Send size={16} /> Nouvelle demande de congé
          </button>
        )}

        {/* Formulaire */}
        {showForm && (
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <Calendar size={16} className="text-gold-600" /> Demande de congé / absence
            </h3>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Type</label>
                <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                  {ABSENCE_TYPES.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Date de début</label>
                  <input type="date" value={form.date_debut}
                    onChange={e => setForm(f => ({ ...f, date_debut: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" required />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Date de fin</label>
                  <input type="date" value={form.date_fin}
                    onChange={e => setForm(f => ({ ...f, date_fin: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" required />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Nombre de jours</label>
                <input type="number" step="0.5" value={form.jours}
                  onChange={e => setForm(f => ({ ...f, jours: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Ex: 2.5" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Commentaire (optionnel)</label>
                <textarea value={form.commentaire}
                  onChange={e => setForm(f => ({ ...f, commentaire: e.target.value }))}
                  rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                  placeholder="Motif, précisions..." />
              </div>
              <div className="flex gap-2 pt-1">
                <button type="submit" disabled={submitting}
                  className="flex-1 bg-navy-700 hover:bg-navy-800 text-white py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50">
                  {submitting ? <Loader size={14} className="animate-spin" /> : <Send size={14} />}
                  Envoyer la demande
                </button>
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-600 rounded-lg text-sm">
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Planning — navigable par semaine */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between gap-2">
            <h3 className="font-semibold text-slate-700 text-sm flex items-center gap-2">
              <Clock size={14} className="text-gold-600" /> Mon planning
            </h3>
            <div className="flex items-center gap-1">
              <button onClick={() => shiftWeek(-1)} disabled={wkLoading} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40" aria-label="Semaine précédente"><ChevronLeft size={16} /></button>
              <span className="text-xs text-slate-600 min-w-[104px] text-center">
                {wkLoading ? '…' : <>Sem. du <strong>{wk.lundi ? format(parseISO(wk.lundi), 'd MMM', { locale: fr }) : '—'}</strong></>}
              </span>
              <button onClick={() => shiftWeek(1)} disabled={wkLoading} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40" aria-label="Semaine suivante"><ChevronRight size={16} /></button>
              {!isCurrentWeek && <button onClick={() => loadWeek()} disabled={wkLoading} className="text-[11px] text-navy-600 hover:underline ml-1">Auj.</button>}
            </div>
          </div>
          {wk.planning.length === 0 ? (
            <div className="py-6 text-center text-slate-400 text-sm">Aucun créneau cette semaine</div>
          ) : (
            <>
              <div className="divide-y divide-slate-100">
                {wk.planning.map(slot => (
                  <div key={slot.id} className="px-4 py-2.5 flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">
                      {format(parseISO(slot.date), 'EEEE d MMM', { locale: fr })}{slot.poste ? ` · ${slot.poste}` : ''}
                    </span>
                    <span className="text-sm text-navy-700 font-medium">{slot.heure_debut} → {slot.heure_fin}</span>
                  </div>
                ))}
              </div>
              <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-right text-xs text-slate-500">
                Total : <strong className="text-navy-700">{wk.heures}h</strong>
              </div>
            </>
          )}
        </div>

        {/* Historique absences */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
            <h3 className="font-semibold text-slate-700 text-sm flex items-center gap-2">
              <Calendar size={14} className="text-gold-600" /> Mes absences
            </h3>
          </div>
          {absences.length === 0 ? (
            <div className="py-8 text-center text-slate-400 text-sm">Aucune absence enregistrée</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {absences.map(a => {
                const statut = STATUT_BADGE[a.statut] || STATUT_BADGE.en_attente;
                const StatutIcon = statut.icon;
                return (
                  <div key={a.id} className="px-4 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-medium text-slate-700">
                          {ABSENCE_TYPES.find(t => t.value === a.type)?.icon} {ABSENCE_TYPES.find(t => t.value === a.type)?.label || a.type}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${statut.color}`}>
                          <StatutIcon size={11} /> {statut.label}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">
                        {formatDate(a.date_debut)} → {formatDate(a.date_fin)}
                        {a.jours ? ` · ${a.jours}j` : ''}
                      </p>
                      {a.commentaire && <p className="text-xs text-slate-400 mt-0.5 truncate">{a.commentaire}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Infos profil */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="font-semibold text-slate-700 text-sm mb-3 flex items-center gap-2">
            <User size={14} className="text-gold-600" /> Mon profil
          </h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-slate-400">Poste :</span> <span className="text-slate-700">{data.poste || '—'}</span></div>
            <div><span className="text-slate-400">Contrat :</span> <span className="text-slate-700">{data.type_contrat}</span></div>
            <div><span className="text-slate-400">Structure :</span> <span className="text-slate-700">{data.structure_nom}</span></div>
            <div><span className="text-slate-400">Email :</span> <span className="text-slate-700">{data.email || '—'}</span></div>
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 py-4">
          SAS Les Cachettes · Hello Georges
        </p>
      </div>
    </div>
  );
}

// Deux usages :
//  • `auto` (compte nominatif connecté) → /api/espace-employe/me, sans saisie de code ;
//  • sinon, accès libre par code personnel (GRG-XXXX) depuis /espace-employe.
export default function EspaceEmployePage({ auto = false, onLogout }) {
  const [base, setBase] = useState(auto ? '/api/espace-employe/me' : '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(auto);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!auto) return;
    let annule = false;
    axios.get('/api/espace-employe/me')
      .then(res => { if (!annule) { setData(res.data); setLoading(false); } })
      .catch(err => {
        if (annule) return;
        setError(err.response?.data?.error || 'Espace indisponible.');
        setLoading(false);
      });
    return () => { annule = true; };
  }, [auto]);

  const handleLogin = async (inputCode) => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`/api/espace-employe/${inputCode}`);
      setBase(`/api/espace-employe/${inputCode}`);
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Code invalide. Vérifiez auprès de la direction.');
      } else {
        setError('Erreur de connexion.');
      }
    }
    setLoading(false);
  };

  const handleLogout = () => {
    if (onLogout) return onLogout();
    setBase(''); setData(null);
  };

  if (auto && !data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-slate-500 gap-2">
        {loading ? <><Loader size={18} className="animate-spin" /> Chargement de votre espace…</> : <span className="text-red-500 text-sm">{error}</span>}
      </div>
    );
  }

  if (!data) {
    return <LoginForm onLogin={handleLogin} loading={loading} error={error} />;
  }

  return <EmployeeDashboard data={data} base={base} onLogout={handleLogout} />;
}
